/**
 * The single data door for the whole UI (DESIGN §8).
 *
 * Pages never touch wagmi for market data — they call `useMarket()`. That is what
 * lets the same components render synthesised numbers today and on-chain reads
 * later: only this file knows which is which.
 *
 * ## Why an external store rather than state
 *
 * The terminal ticks twice a second. Holding the world in `useState` would make
 * every tick re-render every consumer through the context, so the world lives in
 * a `useSyncExternalStore` store: one snapshot swap per tick, callbacks memoised
 * to identity-stable functions, and components that only read callbacks never
 * re-render at all.
 *
 * ## Two implementations behind one interface
 *
 * `MarketStore` serves the synthetic world from `src/lib/demo` and ticks it.
 * `LiveStore` serves real reads through `src/lib/chain`. The provider picks one at
 * mount and nothing downstream can tell which it got.
 *
 * ## Honesty rule
 *
 * When demo mode is OFF, nothing synthetic is ever served. Numbers the chain does
 * not have come back as zero or empty, `error` explains a failed load, and the two
 * demo-only mutations (`submitTrade`, `launchToken`) THROW rather than pretend.
 * Showing invented prices as on-chain truth is the one thing the design forbids
 * outright.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAccount, useBalance, useBlockNumber, useChainId, usePublicClient } from 'wagmi';
import type { Address, PublicClient } from 'viem';
import { chainName, getFactoryAddress } from '@/config/contracts';
import { weiToNumber } from '@/lib/format';
import {
  buildCandlesFromTrades,
  buildFloorSparklineFromTrades,
  buildSparklineFromTrades,
  byChainOrder,
  deriveHolders,
  fetchHolders,
  fetchMarket,
  fetchPositions,
  fetchQuotes,
  fetchStats,
  fetchTrades,
  type LiveMarket,
} from '@/lib/chain';
import {
  applyCurveState,
  buildCandles,
  buildFloorSparkline,
  buildSparkline,
  buyExactIn,
  curveStateFromToken,
  EMPTY_QUOTE,
  executeBuyback,
  floorPrice as floorPriceOf,
  getWorld,
  invalidateSeries,
  makeLaunchedToken,
  makeRng,
  redeemAtFloor,
  sellExactIn,
  spotPrice,
  type CurveState,
  type GeneratedWorld,
  type LaunchInput,
  type Quote,
  type Rng,
} from '@/lib/demo';
import type {
  ActivityKind,
  Candle,
  CandleInterval,
  DemoActivity,
  DemoHolder,
  DemoPosition,
  DemoToken,
  DemoTrade,
  DemoUser,
  Hex,
  ProtocolStats,
  SeriesPoint,
  TradeSide,
} from '@/lib/demo/types';

export type { LaunchInput, Quote };

export interface MarketApi {
  /** True when the numbers on screen are synthesised. The header must say so. */
  isDemo: boolean;
  ready: boolean;
  chainId: number;
  /**
   * Set when the live path could not load — an RPC failure, or no factory
   * deployed on the selected chain. The UI must surface it rather than render an
   * empty market that reads as a bug. Always undefined in demo mode.
   */
  error?: string;

  tokens: DemoToken[];
  stats: ProtocolStats;
  user: DemoUser;
  feed: DemoActivity[];
  floorLockedSeries: SeriesPoint[];

  getToken(address?: string): DemoToken | undefined;
  getCandles(address?: string, interval?: CandleInterval): Candle[];
  getTrades(address?: string, limit?: number): DemoTrade[];
  getHolders(address?: string, limit?: number): DemoHolder[];
  getSparkline(address?: string): number[];
  getFloorSparkline(address?: string): number[];

  quoteBuy(address: string | undefined, ethIn: number): Quote;
  quoteSell(address: string | undefined, tokensIn: number): Quote;
  quoteRedeem(address: string | undefined, tokensIn: number): { ethOut: number; floorPrice: number };

  /**
   * DEMO ONLY. Applies a trade to the in-memory world so the UI reacts instantly.
   * On the live path this THROWS: a real trade is a wallet signature, and that
   * lives in `src/hooks/useContractWrite.ts`. Both call sites already wrap this in
   * try/catch and surface an error toast, so a throw degrades honestly instead of
   * reporting a fill that never happened.
   */
  submitTrade(input: {
    address: string;
    side: TradeSide;
    ethAmount?: number;
    tokenAmount?: number;
  }): DemoTrade;
  /** DEMO ONLY, same boundary as {@link submitTrade} — see `useCreateTokenAndBuy`. */
  launchToken(input: LaunchInput): DemoToken;
}

/* ────────────────────────────────────────────────────────────────────────────
   Demo-mode resolution
   ──────────────────────────────────────────────────────────────────────────── */

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000' as Hex;
const ZERO_HASH = `0x${'0'.repeat(64)}` as Hex;

function resolveDemoMode(): boolean {
  // `import.meta.env` is typed with a project-specific interface that does not
  // declare VITE_DEMO, so read it through an index signature rather than editing
  // a file this module does not own.
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  let demo = env.VITE_DEMO !== 'false';
  if (typeof window !== 'undefined') {
    const forced = new URLSearchParams(window.location.search).get('demo');
    if (forced === '1' || forced === 'true') demo = true;
    else if (forced === '0' || forced === 'false') demo = false;
  }
  return demo;
}

function emptyWorld(): GeneratedWorld {
  const stats: ProtocolStats = {
    tokensLaunched: 0,
    volumeAllTimeEth: 0,
    volume24hEth: 0,
    floorLockedEth: 0,
    ethReturnedEth: 0,
    graduatedCount: 0,
    tradersCount: 0,
    ethBurnedEth: 0,
    volume24hChange: 0,
    floorLockedChange: 0,
  };
  const user: DemoUser = {
    address: ZERO_ADDRESS,
    handle: '',
    balanceEth: 0,
    joinedAt: Date.now(),
    positions: [],
    launches: [],
    activity: [],
    realizedPnlEth: 0,
    feesEarnedEth: 0,
  };
  return {
    tokens: [],
    stats,
    user,
    feed: [],
    floorLockedSeries: [],
    trades: new Map(),
    holders: new Map(),
    curves: new Map(),
    traders: new Map(),
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Store
   ──────────────────────────────────────────────────────────────────────────── */

interface Snapshot {
  tokens: DemoToken[];
  stats: ProtocolStats;
  user: DemoUser;
  feed: DemoActivity[];
  floorLockedSeries: SeriesPoint[];
}

const FEED_CAP = 220;
const TAPE_CAP = 400;

class MarketStore {
  private world: GeneratedWorld;
  private snap: Snapshot;
  private listeners = new Set<() => void>();
  private rng: Rng;
  private seq = 0;

  constructor(world: GeneratedWorld) {
    this.world = world;
    this.rng = makeRng('bull.markets/live-tick');
    this.snap = {
      tokens: world.tokens,
      stats: world.stats,
      user: world.user,
      feed: world.feed,
      floorLockedSeries: world.floorLockedSeries,
    };
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): Snapshot => this.snap;

  /** One snapshot object per mutation — the only thing consumers can observe. */
  private commit(next: Partial<Snapshot>): void {
    this.snap = { ...this.snap, ...next };
    for (const l of this.listeners) l();
  }

  private key(address?: string): string | undefined {
    return address && address.startsWith('0x') ? address.toLowerCase() : undefined;
  }

  getToken = (address?: string): DemoToken | undefined => {
    const key = this.key(address);
    if (!key) return undefined;
    return this.snap.tokens.find((t) => t.address.toLowerCase() === key);
  };

  private curveFor(token: DemoToken): CurveState {
    const key = token.address.toLowerCase();
    const hit = this.world.curves.get(key);
    if (hit) return hit;
    const built = curveStateFromToken(token);
    this.world.curves.set(key, built);
    return built;
  }

  getCandles = (address?: string, interval: CandleInterval = '1h'): Candle[] => {
    const token = this.getToken(address);
    return token ? buildCandles(token, interval) : [];
  };

  getTrades = (address?: string, limit = 60): DemoTrade[] => {
    const key = this.key(address);
    if (!key) return [];
    return (this.world.trades.get(key) ?? []).slice(0, Math.max(0, limit));
  };

  getHolders = (address?: string, limit = 25): DemoHolder[] => {
    const key = this.key(address);
    if (!key) return [];
    return (this.world.holders.get(key) ?? []).slice(0, Math.max(0, limit));
  };

  getSparkline = (address?: string): number[] => {
    const token = this.getToken(address);
    return token ? buildSparkline(token) : [];
  };

  getFloorSparkline = (address?: string): number[] => {
    const token = this.getToken(address);
    return token ? buildFloorSparkline(token) : [];
  };

  quoteBuy = (address: string | undefined, ethIn: number): Quote => {
    const token = this.getToken(address);
    if (!token || !(ethIn > 0)) return this.zeroQuote(token);
    return buyExactIn(this.curveFor(token), ethIn).quote;
  };

  quoteSell = (address: string | undefined, tokensIn: number): Quote => {
    const token = this.getToken(address);
    if (!token || !(tokensIn > 0)) return this.zeroQuote(token);
    return sellExactIn(this.curveFor(token), tokensIn).quote;
  };

  quoteRedeem = (address: string | undefined, tokensIn: number): { ethOut: number; floorPrice: number } => {
    const token = this.getToken(address);
    if (!token) return { ethOut: 0, floorPrice: 0 };
    const state = this.curveFor(token);
    const fp = floorPriceOf(state);
    return { ethOut: Math.max(0, Math.min(tokensIn, state.redeemableSupply)) * fp, floorPrice: fp };
  };

  /** A zero quote still carries live prices, so the panel can render a state. */
  private zeroQuote(token?: DemoToken): Quote {
    if (!token) return EMPTY_QUOTE;
    const state = this.curveFor(token);
    return { ...EMPTY_QUOTE, newSpotPrice: spotPrice(state), newFloorPrice: floorPriceOf(state) };
  }

  /* ── mutations ──────────────────────────────────────────────────────────── */

  private replaceToken(next: DemoToken, state: CurveState): void {
    this.world.curves.set(next.address.toLowerCase(), state);
    const tokens = this.snap.tokens.slice();
    const i = tokens.findIndex((t) => t.address.toLowerCase() === next.address.toLowerCase());
    if (i >= 0) tokens[i] = next;
    else tokens.unshift(next);

    // Positions and launches embed the token object, so they have to follow it or
    // the portfolio would keep rendering a stale price.
    const user = this.snap.user;
    const positions = user.positions.map((p) =>
      p.token.address === next.address ? repriced(p, next) : p,
    );
    const launches = user.launches.map((t) => (t.address === next.address ? next : t));

    this.commit({
      tokens,
      user: { ...user, positions, launches },
      stats: { ...this.snap.stats, floorLockedEth: sum(tokens, (t) => t.floorLockedEth) },
    });
  }

  submitTrade = (input: {
    address: string;
    side: TradeSide;
    ethAmount?: number;
    tokenAmount?: number;
  }): DemoTrade => {
    const token = this.getToken(input.address);
    const now = Date.now();
    if (!token) {
      // The contract would revert; the UI must not. Hand back an inert receipt.
      return {
        id: `noop-${this.seq++}`,
        tokenAddress: (input.address as Hex) ?? ZERO_ADDRESS,
        side: input.side,
        trader: this.snap.user.address,
        ethAmount: 0,
        tokenAmount: 0,
        price: 0,
        ts: now,
        txHash: ZERO_HASH,
      };
    }

    const state = this.curveFor(token);
    const spot = spotPrice(state);
    let next = state;
    let ethAmount = 0;
    let tokenAmount = 0;
    let floorLiftEth: number | undefined;

    if (input.side === 'buy') {
      ethAmount = Math.max(0, input.ethAmount ?? (input.tokenAmount ?? 0) * spot);
      const res = buyExactIn(state, ethAmount, now);
      next = res.next;
      tokenAmount = res.quote.tokensOut;
      floorLiftEth = res.quote.floorAddEth;
    } else if (input.side === 'sell') {
      tokenAmount = Math.max(0, input.tokenAmount ?? (spot > 0 ? (input.ethAmount ?? 0) / spot : 0));
      const res = sellExactIn(state, tokenAmount);
      next = res.next;
      ethAmount = res.quote.ethOut;
    } else if (input.side === 'redeem') {
      tokenAmount = Math.max(0, input.tokenAmount ?? 0);
      const res = redeemAtFloor(state, tokenAmount);
      next = res.next;
      ethAmount = res.ethOut;
    } else {
      const res = executeBuyback(state);
      next = res.next;
      tokenAmount = res.tokensBurned;
      ethAmount = res.quote.avgPrice * res.tokensBurned;
      floorLiftEth = res.quote.floorAddEth;
    }

    const price = tokenAmount > 0 ? ethAmount / tokenAmount : spot;
    const trade: DemoTrade = {
      id: `live-${this.seq++}`,
      tokenAddress: token.address,
      side: input.side,
      trader: this.snap.user.address,
      traderHandle: this.snap.user.handle || undefined,
      ethAmount,
      tokenAmount,
      price,
      ts: now,
      txHash: this.rng.txHash(),
      floorLiftEth,
      antiSnipe: input.side === 'buy' && now < token.createdAt + token.antiSnipeWindow * 2000 ? true : undefined,
    };

    const nextToken = applyCurveState(token, next, now);
    nextToken.volume24hEth = token.volume24hEth + ethAmount;
    nextToken.trades24h = token.trades24h + 1;
    if (input.side === 'buy' || input.side === 'buyback') nextToken.floorLifts = token.floorLifts + 1;

    const key = token.address.toLowerCase();
    this.world.trades.set(key, [trade, ...(this.world.trades.get(key) ?? [])].slice(0, TAPE_CAP));
    // A user trade genuinely moves the price, so the chart has to be rebuilt.
    invalidateSeries(key);

    this.replaceToken(nextToken, next);
    this.applyToUser(nextToken, input.side, ethAmount, tokenAmount, price);
    this.pushActivity(nextToken, sideToKind(input.side), ethAmount, tokenAmount, this.snap.user.address, this.snap.user.handle);
    return trade;
  };

  /** Moves the user's ETH balance and position to match a trade they just made. */
  private applyToUser(
    token: DemoToken,
    side: TradeSide,
    ethAmount: number,
    tokenAmount: number,
    price: number,
  ): void {
    const user = this.snap.user;
    const positions = user.positions.slice();
    const i = positions.findIndex((p) => p.token.address === token.address);
    const held = i >= 0 ? weiToNumber(positions[i]!.balance) : 0;
    const cost = i >= 0 ? positions[i]!.costEth : 0;

    let balance = held;
    let costEth = cost;
    let balanceEth = user.balanceEth;

    if (side === 'buy') {
      balance = held + tokenAmount;
      costEth = cost + ethAmount;
      balanceEth -= ethAmount;
    } else if (side === 'sell' || side === 'redeem') {
      const sold = Math.min(held, tokenAmount);
      balance = held - sold;
      // Reduce basis proportionally so avgEntry survives a partial exit.
      costEth = held > 0 ? cost * (1 - sold / held) : 0;
      balanceEth += ethAmount;
    } else {
      return; // a buyback is protocol-funded; the user's wallet is untouched
    }

    const spot = weiToNumber(token.spotPrice);
    const floor = weiToNumber(token.floorPrice);
    const position: DemoPosition = {
      token,
      balance: toWeiFloat(balance),
      avgEntry: balance > 0 ? costEth / balance : price,
      costEth,
      valueEth: balance * spot,
      floorValueEth: balance * floor,
      pnlEth: balance * spot - costEth,
      pnlPct: costEth > 0 ? (balance * spot - costEth) / costEth : 0,
    };

    if (i >= 0) {
      if (balance <= 0) positions.splice(i, 1);
      else positions[i] = position;
    } else if (balance > 0) {
      positions.unshift(position);
    }

    this.commit({ user: { ...user, positions, balanceEth: Math.max(0, balanceEth) } });
  }

  launchToken = (input: LaunchInput): DemoToken => {
    const now = Date.now();
    const token = makeLaunchedToken(input, this.snap.user, this.snap.tokens.length, now);
    const state = curveStateFromToken(token);
    const key = token.address.toLowerCase();

    this.world.curves.set(key, state);
    this.world.trades.set(key, []);
    this.world.holders.set(key, []);
    this.world.traders.set(key, [{ address: this.snap.user.address, handle: this.snap.user.handle }]);

    const user = this.snap.user;
    this.commit({
      tokens: [token, ...this.snap.tokens],
      user: {
        ...user,
        launches: [token, ...user.launches],
        balanceEth: Math.max(0, user.balanceEth - Math.max(0, input.devBuyEth)),
      },
      stats: { ...this.snap.stats, tokensLaunched: this.snap.stats.tokensLaunched + 1 },
    });
    this.pushActivity(token, 'launch', undefined, undefined, user.address, user.handle);
    return token;
  };

  private pushActivity(
    token: DemoToken,
    kind: ActivityKind,
    ethAmount: number | undefined,
    tokenAmount: number | undefined,
    actor: Hex,
    actorHandle?: string,
  ): void {
    const entry: DemoActivity = {
      id: `act-${this.seq++}`,
      kind,
      token: {
        address: token.address,
        name: token.name,
        symbol: token.symbol,
        hue: token.hue,
        glyph: token.glyph,
      },
      actor,
      actorHandle: actorHandle || undefined,
      ethAmount,
      tokenAmount,
      ts: Date.now(),
      txHash: this.rng.txHash(),
    };
    this.commit({ feed: [entry, ...this.snap.feed].slice(0, FEED_CAP) });
  }

  /* ── live ticking ───────────────────────────────────────────────────────── */

  /**
   * Nudges a handful of tokens per tick by pushing a *real* micro-trade through
   * the curve rather than fudging the price. That is the only way the floor stays
   * monotone and `k` stays put while the terminal looks alive.
   */
  tick = (): void => {
    const tokens = this.snap.tokens;
    if (tokens.length === 0) return;
    const now = Date.now();
    const nextTokens = tokens.slice();
    let touched = false;
    let volumeDelta = 0;

    const picks = Math.min(8, tokens.length);
    for (let n = 0; n < picks; n++) {
      const idx = this.rng.int(0, tokens.length - 1);
      const token = nextTokens[idx]!;
      if (token.tradingStopped) continue;

      const state = this.curveFor(token);
      const size = Math.max(1e-7, token.raisedEth * 0.0004 * Math.exp(this.rng.gauss(0, 0.9)));
      const buying = this.rng.bool(token.change24h >= 0 ? 0.55 : 0.45);
      const res = buying
        ? buyExactIn(state, size, now)
        : sellExactIn(state, size / Math.max(1e-18, spotPrice(state)));

      // A sell can mathematically price below the floor; economically it cannot,
      // because redeem-at-floor is a standing bid. Skip the tick instead.
      if (spotPrice(res.next) < floorPriceOf(res.next)) continue;

      const updated = applyCurveState(token, res.next, now);
      updated.volume24hEth = token.volume24hEth + size;
      nextTokens[idx] = updated;
      this.world.curves.set(token.address.toLowerCase(), res.next);
      volumeDelta += size;
      touched = true;
    }

    if (!touched) return;
    this.commit({
      tokens: nextTokens,
      stats: {
        ...this.snap.stats,
        volume24hEth: this.snap.stats.volume24hEth + volumeDelta,
        floorLockedEth: sum(nextTokens, (t) => t.floorLockedEth),
      },
    });
  };

  /** Drops one synthetic trade into the cross-token firehose. */
  tickFeed = (): void => {
    const tokens = this.snap.tokens;
    if (tokens.length === 0) return;
    const token = this.rng.pickWeighted(tokens, tokens.map((t) => Math.max(0.01, t.volume24hEth)));
    const pool = this.world.traders.get(token.address.toLowerCase()) ?? [];
    const actor = pool.length > 0 ? pool[this.rng.int(0, pool.length - 1)]! : { address: ZERO_ADDRESS };
    const kinds: ActivityKind[] = token.tradingStopped
      ? ['redeem', 'claim']
      : ['buy', 'sell', 'floor-lift', 'redeem', 'buyback'];
    const weights = token.tradingStopped ? [8, 2] : [46, 26, 14, 6, 3];
    const kind = this.rng.pickWeighted(kinds, weights);
    const ethAmount = Math.max(0.0002, Math.abs(this.rng.gauss(0, 1)) * Math.max(0.002, token.raisedEth * 0.015));
    const price = weiToNumber(token.spotPrice) || 1e-12;
    this.pushActivity(token, kind, ethAmount, ethAmount / price, actor.address, 'handle' in actor ? actor.handle : undefined);
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────────────────────────── */

function sum<T>(items: readonly T[], get: (item: T) => number): number {
  let total = 0;
  for (const item of items) total += get(item);
  return total;
}

/** Local wei conversion; `parseEther` rejects exponential strings. */
function toWeiFloat(n: number): bigint {
  if (!Number.isFinite(n) || n <= 0) return 0n;
  return BigInt(Math.round(n * 1e6)) * 10n ** 12n;
}

function repriced(position: DemoPosition, token: DemoToken): DemoPosition {
  const balance = weiToNumber(position.balance);
  const valueEth = balance * weiToNumber(token.spotPrice);
  return {
    ...position,
    token,
    valueEth,
    floorValueEth: balance * weiToNumber(token.floorPrice),
    pnlEth: valueEth - position.costEth,
    pnlPct: position.costEth > 0 ? (valueEth - position.costEth) / position.costEth : 0,
  };
}

function sideToKind(side: TradeSide): ActivityKind {
  switch (side) {
    case 'buy':
      return 'buy';
    case 'sell':
      return 'sell';
    case 'redeem':
      return 'redeem';
    default:
      return 'buyback';
  }
}

/* ────────────────────────────────────────────────────────────────────────────
   Live store
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * The on-chain implementation of the same read surface.
 *
 * ## Why the getters are still synchronous
 *
 * `MarketApi` is synchronous by design — components call `getCandles()` in render
 * and get an array. A chain is not synchronous. So the contract here is
 * **read-through cache with a deferred fill**:
 *
 *   1. `getTrades` / `getHolders` / `getCandles` return whatever is cached, which
 *      on the very first call for a token is the windowed tape that came with the
 *      market refresh — or, for a token that has never traded, `[]`.
 *   2. The same call schedules a deep per-token fetch (full history from the
 *      token's creation block) on a microtask, so nothing is fetched during
 *      render.
 *   3. When that lands it is committed to the store, subscribers are notified,
 *      and the component re-renders with the real thing.
 *
 * So a token page paints in one frame from the market-wide scan and fills in
 * ~100ms later. Components need no loading state for this — the numbers only ever
 * get *more* complete, never wrong.
 *
 * ## Cache invalidation
 *
 * Everything derived is memoised against the block number the market was read
 * at. A refresh bumps that number and the derived maps are dropped, so nothing
 * can serve a value computed from stale reserves. Deep tapes are the exception:
 * they are kept and MERGED with each new windowed tape, because re-scanning the
 * full history every 12s would be wasteful and would make an open chart flicker
 * as its history shrank and regrew.
 */
class LiveStore {
  private snap: Snapshot = emptySnapshot();
  private listeners = new Set<() => void>();

  private client?: PublicClient;
  private blockNumber = 0n;
  private creationBlocks = new Map<string, bigint>();

  /** Tapes from the market-wide log scan; replaced wholesale on every refresh. */
  private windowed = new Map<string, DemoTrade[]>();
  /** Full-history tapes from a per-token scan; kept across refreshes and merged. */
  private deep = new Map<string, DemoTrade[]>();

  private holderCache = new Map<string, DemoHolder[]>();
  private candleCache = new Map<string, Candle[]>();
  private sparkCache = new Map<string, { price: number[]; floor: number[] }>();
  /** Contract-truth `tokensOut` / `quoteOut`, keyed by address + input size. */
  private chainQuotes = new Map<string, bigint>();
  private inflight = new Set<string>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): Snapshot => this.snap;

  private commit(next: Partial<Snapshot>): void {
    this.snap = { ...this.snap, ...next };
    for (const l of this.listeners) l();
  }

  private notify(): void {
    for (const l of this.listeners) l();
  }

  private key(address?: string): string | undefined {
    return address && address.startsWith('0x') ? address.toLowerCase() : undefined;
  }

  /* ── ingest ─────────────────────────────────────────────────────────────── */

  setClient(client: PublicClient | undefined): void {
    this.client = client;
  }

  applyMarket(live: LiveMarket): void {
    this.blockNumber = live.blockNumber;
    this.creationBlocks = live.creationBlocks;
    this.windowed = live.trades;

    // Fold the fresh window into any deep tape so an open token page stays live
    // without paying for another full-history scan.
    for (const [key, tape] of live.trades) {
      const deep = this.deep.get(key);
      if (deep) this.deep.set(key, mergeTapes(deep, tape));
    }

    // Everything derived was computed against the previous block. Drop it.
    this.holderCache.clear();
    this.candleCache.clear();
    this.sparkCache.clear();
    this.chainQuotes.clear();

    this.commit({
      tokens: live.tokens,
      stats: live.stats,
      feed: live.feed,
      floorLockedSeries: live.floorLockedSeries,
    });
  }

  applyUser(user: DemoUser): void {
    this.commit({ user });
  }

  /** Clears the market but keeps the store identity (chain switch, factory swap). */
  reset(): void {
    this.windowed = new Map();
    this.deep = new Map();
    this.holderCache.clear();
    this.candleCache.clear();
    this.sparkCache.clear();
    this.chainQuotes.clear();
    this.inflight.clear();
    this.snap = emptySnapshot();
    this.notify();
  }

  /* ── reads ──────────────────────────────────────────────────────────────── */

  getToken = (address?: string): DemoToken | undefined => {
    const key = this.key(address);
    if (!key) return undefined;
    return this.snap.tokens.find((t) => t.address.toLowerCase() === key);
  };

  private tapeFor(key: string): DemoTrade[] {
    return this.deep.get(key) ?? this.windowed.get(key) ?? [];
  }

  getTrades = (address?: string, limit = 60): DemoTrade[] => {
    const key = this.key(address);
    if (!key) return [];
    this.ensureDeepTape(key);
    return this.tapeFor(key).slice(0, Math.max(0, limit));
  };

  getHolders = (address?: string, limit = 25): DemoHolder[] => {
    const key = this.key(address);
    const token = this.getToken(address);
    if (!key || !token) return [];
    this.ensureDeepTape(key);
    this.ensureHolders(key, token);

    const cacheKey = `${key}:${this.blockNumber}`;
    let rows = this.holderCache.get(cacheKey);
    if (!rows) {
      // Until the Transfer scan lands, net the tape we already have. Approximate
      // (it cannot see wallet-to-wallet transfers) but immediate and honest.
      rows = deriveHolders(this.tapeFor(key), token);
      this.holderCache.set(cacheKey, rows);
    }
    return rows.slice(0, Math.max(0, limit));
  };

  getCandles = (address?: string, interval: CandleInterval = '1h'): Candle[] => {
    const key = this.key(address);
    const token = this.getToken(address);
    if (!key || !token) return [];
    this.ensureDeepTape(key);

    const cacheKey = `${key}:${interval}:${this.blockNumber}`;
    const hit = this.candleCache.get(cacheKey);
    if (hit) return hit;
    const built = buildCandlesFromTrades(this.tapeFor(key), interval, token);
    this.candleCache.set(cacheKey, built);
    return built;
  };

  getSparkline = (address?: string): number[] => this.spark(address).price;
  getFloorSparkline = (address?: string): number[] => this.spark(address).floor;

  private spark(address?: string): { price: number[]; floor: number[] } {
    const key = this.key(address);
    const token = this.getToken(address);
    if (!key || !token) return { price: [], floor: [] };
    const cacheKey = `${key}:${this.blockNumber}`;
    const hit = this.sparkCache.get(cacheKey);
    if (hit) return hit;
    const tape = this.tapeFor(key);
    const built = {
      price: buildSparklineFromTrades(tape, token),
      floor: buildFloorSparklineFromTrades(tape, token),
    };
    this.sparkCache.set(cacheKey, built);
    return built;
  }

  /* ── quotes ─────────────────────────────────────────────────────────────── */

  /**
   * `quoteBuyExactIn` / `quoteSellExactIn` are the ground truth: they price the
   * anti-snipe slice at the *current block*, which the float mirror can only
   * estimate from wall-clock (DESIGN §10 — the window is measured in blocks).
   *
   * So the mirror answers instantly and the contract corrects it: the returned
   * quote is the mirror's, with `tokensOut` / `ethOut` overwritten once the read
   * lands. Every other field (fee split, floor add, price impact, resulting
   * prices) is arithmetic the contract does not expose as a view, so it stays the
   * mirror's — computed from reserves the contract just gave us, not invented.
   */
  quoteBuy = (address: string | undefined, ethIn: number): Quote => {
    const token = this.getToken(address);
    if (!token || !(ethIn > 0)) return this.zeroQuote(token);
    const quote = buyExactIn(curveStateFromToken(token), ethIn).quote;
    const exact = this.chainQuote(token, 'buy', ethIn);
    if (exact !== undefined && exact > 0) {
      const tokensOut = weiToNumber(exact);
      return { ...quote, tokensOut, avgPrice: tokensOut > 0 ? ethIn / tokensOut : quote.avgPrice };
    }
    return quote;
  };

  quoteSell = (address: string | undefined, tokensIn: number): Quote => {
    const token = this.getToken(address);
    if (!token || !(tokensIn > 0)) return this.zeroQuote(token);
    const quote = sellExactIn(curveStateFromToken(token), tokensIn).quote;
    const exact = this.chainQuote(token, 'sell', tokensIn);
    if (exact !== undefined && exact > 0) {
      const ethOut = weiToNumber(exact);
      return { ...quote, ethOut, avgPrice: tokensIn > 0 ? ethOut / tokensIn : quote.avgPrice };
    }
    return quote;
  };

  /**
   * Exact with no read at all: `redeemAtFloor` pays `amount × floorPrice` flat,
   * and `floorPrice` is one of the getters we already hold.
   */
  quoteRedeem = (address: string | undefined, tokensIn: number): { ethOut: number; floorPrice: number } => {
    const token = this.getToken(address);
    if (!token) return { ethOut: 0, floorPrice: 0 };
    const fp = weiToNumber(token.floorPrice);
    const redeemable = weiToNumber(token.redeemableSupply);
    return { ethOut: Math.max(0, Math.min(tokensIn, redeemable)) * fp, floorPrice: fp };
  };

  private zeroQuote(token?: DemoToken): Quote {
    if (!token) return EMPTY_QUOTE;
    return {
      ...EMPTY_QUOTE,
      newSpotPrice: weiToNumber(token.spotPrice),
      newFloorPrice: weiToNumber(token.floorPrice),
    };
  }

  /** Cached contract quote for one input size; schedules the read on a miss. */
  private chainQuote(token: DemoToken, side: 'buy' | 'sell', amount: number): bigint | undefined {
    const key = `${token.address.toLowerCase()}:${side}:${amount.toPrecision(9)}:${this.blockNumber}`;
    const hit = this.chainQuotes.get(key);
    if (hit !== undefined) return hit;
    this.schedule(key, async (client) => {
      const wei = toWeiFloat(amount);
      if (wei <= 0n) return;
      const res = await fetchQuotes(
        client,
        token.address as Address,
        side === 'buy' ? wei : 0n,
        side === 'sell' ? wei : 0n,
      );
      this.chainQuotes.set(key, side === 'buy' ? res.tokensOut : res.quoteOut);
      this.notify();
    });
    return undefined;
  }

  /* ── deferred fills ─────────────────────────────────────────────────────── */

  /**
   * Full history for one token, from its creation block. Runs at most once per
   * token per session — later refreshes merge into it rather than re-scanning.
   */
  private ensureDeepTape(key: string): void {
    if (this.deep.has(key)) return;
    const token = this.snap.tokens.find((t) => t.address.toLowerCase() === key);
    if (!token) return;
    const fromBlock = this.creationBlocks.get(key) ?? 0n;
    this.schedule(`tape:${key}`, async (client) => {
      const tape = await fetchTrades(client, token.address as Address, fromBlock, {
        toBlock: this.blockNumber > 0n ? this.blockNumber : undefined,
      });
      const windowed = this.windowed.get(key) ?? [];
      this.deep.set(key, mergeTapes(tape, windowed));
      // The tape feeds candles, sparklines and the netted holder list.
      this.candleCache.clear();
      this.sparkCache.clear();
      this.holderCache.clear();
      this.notify();
    });
  }

  /** Accurate holder rows from a full `Transfer` scan; one per token per block. */
  private ensureHolders(key: string, token: DemoToken): void {
    const cacheKey = `${key}:${this.blockNumber}`;
    this.schedule(`holders:${cacheKey}`, async (client) => {
      const rows = await fetchHolders(client, token, this.creationBlocks.get(key) ?? 0n, {
        toBlock: this.blockNumber > 0n ? this.blockNumber : undefined,
        trades: this.tapeFor(key),
      });
      this.holderCache.set(cacheKey, rows);
      this.notify();
    });
  }

  /**
   * Deferred so nothing is fetched during render, de-duplicated so a component
   * that re-renders ten times does not issue ten scans. Failures are swallowed:
   * a missing tape degrades a chart, it must not break the page.
   */
  private schedule(key: string, run: (client: PublicClient) => Promise<void>): void {
    if (this.inflight.has(key)) return;
    const client = this.client;
    if (!client) return;
    this.inflight.add(key);
    queueMicrotask(() => {
      void run(client)
        .catch(() => undefined)
        .finally(() => {
          this.inflight.delete(key);
        });
    });
  }

  /* ── mutations: not ours ────────────────────────────────────────────────── */

  submitTrade = (): DemoTrade => {
    throw new Error(
      'submitTrade is demo-only. On a live chain a trade is a wallet transaction — use the write hooks in src/hooks/useContractWrite.ts.',
    );
  };

  launchToken = (): DemoToken => {
    throw new Error(
      'launchToken is demo-only. On a live chain a launch is a wallet transaction — use useCreateTokenAndBuy from src/hooks/useContractWrite.ts.',
    );
  };
}

function emptySnapshot(): Snapshot {
  const world = emptyWorld();
  return {
    tokens: world.tokens,
    stats: world.stats,
    user: world.user,
    feed: world.feed,
    floorLockedSeries: world.floorLockedSeries,
  };
}

/**
 * Newest-first union of two tapes, de-duplicated on `id`.
 *
 * `id` is `(blockNumber, logIndex)` zero-padded, so it is both a globally unique
 * key for a log and a chain-order sort key — which is what the tape must be
 * ordered by, since every trade in a block shares that block's timestamp.
 */
function mergeTapes(a: readonly DemoTrade[], b: readonly DemoTrade[]): DemoTrade[] {
  const byId = new Map<string, DemoTrade>();
  for (const t of a) byId.set(t.id, t);
  for (const t of b) byId.set(t.id, t);
  return [...byId.values()].sort(byChainOrder);
}

/* ────────────────────────────────────────────────────────────────────────────
   Provider
   ──────────────────────────────────────────────────────────────────────────── */

const MarketContext = createContext<MarketApi | null>(null);

/**
 * Resolved once per page load, at module scope, so the provider can branch on it
 * without ever changing which sub-tree (and therefore which set of hooks) it
 * renders. `?demo=` is read at load; a change to it is a navigation.
 */
const IS_DEMO = resolveDemoMode();

export function MarketProvider({ children }: { children: ReactNode }): JSX.Element {
  return IS_DEMO ? (
    <DemoMarketProvider>{children}</DemoMarketProvider>
  ) : (
    <LiveMarketProvider>{children}</LiveMarketProvider>
  );
}

function DemoMarketProvider({ children }: { children: ReactNode }): JSX.Element {
  const chainId = useChainId();
  const isDemo = true;

  const storeRef = useRef<MarketStore | null>(null);
  if (!storeRef.current) storeRef.current = new MarketStore(getWorld());
  const store = storeRef.current;

  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  useEffect(() => {
    if (!isDemo) return;

    const motion =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;

    let priceTimer: number | undefined;
    let feedTimer: number | undefined;

    const stop = (): void => {
      if (priceTimer !== undefined) window.clearInterval(priceTimer);
      if (feedTimer !== undefined) window.clearInterval(feedTimer);
      priceTimer = undefined;
      feedTimer = undefined;
    };

    const start = (): void => {
      stop();
      // Reduced motion does not mean "frozen" — it means "do not animate at me".
      // 15s keeps the data live without a moving carpet of numbers.
      const reduced = motion?.matches ?? false;
      priceTimer = window.setInterval(store.tick, reduced ? 15_000 : 2_500);
      feedTimer = window.setInterval(store.tickFeed, reduced ? 15_000 : 2_000);
    };

    // A hidden tab must not burn CPU (and must not queue a hundred ticks that
    // all land at once when it comes back).
    const onVisibility = (): void => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    motion?.addEventListener?.('change', start);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      motion?.removeEventListener?.('change', start);
    };
  }, [isDemo, store]);

  const getToken = useCallback((address?: string) => store.getToken(address), [store]);
  const getCandles = useCallback(
    (address?: string, interval: CandleInterval = '1h') => store.getCandles(address, interval),
    [store],
  );
  const getTrades = useCallback((address?: string, limit?: number) => store.getTrades(address, limit), [store]);
  const getHolders = useCallback((address?: string, limit?: number) => store.getHolders(address, limit), [store]);
  const getSparkline = useCallback((address?: string) => store.getSparkline(address), [store]);
  const getFloorSparkline = useCallback((address?: string) => store.getFloorSparkline(address), [store]);
  const quoteBuy = useCallback((address: string | undefined, ethIn: number) => store.quoteBuy(address, ethIn), [store]);
  const quoteSell = useCallback(
    (address: string | undefined, tokensIn: number) => store.quoteSell(address, tokensIn),
    [store],
  );
  const quoteRedeem = useCallback(
    (address: string | undefined, tokensIn: number) => store.quoteRedeem(address, tokensIn),
    [store],
  );
  const submitTrade = useCallback(
    (input: { address: string; side: TradeSide; ethAmount?: number; tokenAmount?: number }) =>
      store.submitTrade(input),
    [store],
  );
  const launchToken = useCallback((input: LaunchInput) => store.launchToken(input), [store]);

  const value = useMemo<MarketApi>(
    () => ({
      isDemo,
      ready: true,
      chainId,
      tokens: snapshot.tokens,
      stats: snapshot.stats,
      user: snapshot.user,
      feed: snapshot.feed,
      floorLockedSeries: snapshot.floorLockedSeries,
      getToken,
      getCandles,
      getTrades,
      getHolders,
      getSparkline,
      getFloorSparkline,
      quoteBuy,
      quoteSell,
      quoteRedeem,
      submitTrade,
      launchToken,
    }),
    [
      isDemo,
      chainId,
      snapshot,
      getToken,
      getCandles,
      getTrades,
      getHolders,
      getSparkline,
      getFloorSparkline,
      quoteBuy,
      quoteSell,
      quoteRedeem,
      submitTrade,
      launchToken,
    ],
  );

  return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>;
}

/* ────────────────────────────────────────────────────────────────────────────
   Live provider
   ──────────────────────────────────────────────────────────────────────────── */

/** A full market refresh is ~N+3 RPC calls, so 12s is the floor we allow. */
const MARKET_POLL_MS = 12_000;
/** `eth_blockNumber` is the cheapest call there is, so it may run a little hotter. */
const BLOCK_POLL_MS = 12_000;

function LiveMarketProvider({ children }: { children: ReactNode }): JSX.Element {
  const chainId = useChainId();
  const publicClient = usePublicClient({ chainId });
  const { address: account } = useAccount();
  const factory = getFactoryAddress(chainId);
  const hasFactoryHere = factory.toLowerCase() !== ZERO_ADDRESS;
  const client = publicClient as unknown as PublicClient | undefined;

  const storeRef = useRef<LiveStore | null>(null);
  if (!storeRef.current) storeRef.current = new LiveStore();
  const store = storeRef.current;

  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  // A chain switch invalidates every cached tape and every derived series: the
  // addresses do not even mean the same thing on another chain.
  useEffect(() => {
    store.reset();
  }, [store, chainId, factory]);

  useEffect(() => {
    store.setClient(client);
  }, [store, client]);

  const marketQuery = useQuery({
    // Keyed on the pair that decides *which* market this is. Nothing else.
    queryKey: ['bull', 'live-market', chainId, factory],
    queryFn: () => fetchMarket(client as PublicClient, factory as Address),
    enabled: Boolean(client) && hasFactoryHere,
    refetchInterval: MARKET_POLL_MS,
    // Within the poll window a remount should reuse the last read rather than
    // re-issuing N+3 calls.
    staleTime: MARKET_POLL_MS - 2_000,
    retry: 1,
  });

  const { data: liveData, refetch: refetchMarket } = marketQuery;

  useEffect(() => {
    if (liveData) store.applyMarket(liveData);
  }, [store, liveData]);

  /**
   * Block-driven refresh on top of the interval. This is what makes an idle chain
   * cheap (nothing to re-read until a block lands) and a busy one responsive
   * (a trade shows up in ≤12s rather than at the next tick boundary). Throttled,
   * because on a 2s chain a refresh per block would be six times the budget.
   */
  const { data: blockNumber } = useBlockNumber({
    chainId,
    watch: hasFactoryHere ? { pollingInterval: BLOCK_POLL_MS } : false,
    query: { enabled: Boolean(client) && hasFactoryHere },
  });
  const lastBlockRefetch = useRef(Date.now());
  useEffect(() => {
    if (blockNumber === undefined) return;
    const now = Date.now();
    if (now - lastBlockRefetch.current < MARKET_POLL_MS - 2_000) return;
    lastBlockRefetch.current = now;
    void refetchMarket();
  }, [blockNumber, refetchMarket]);

  /* ── the connected wallet ─────────────────────────────────────────────── */

  const { data: nativeBalance } = useBalance({
    address: account,
    chainId,
    query: { enabled: Boolean(account) },
  });

  const positionsQuery = useQuery({
    // Re-keyed per block so a position revalues with the market it belongs to.
    queryKey: ['bull', 'live-positions', chainId, factory, account, liveData?.blockNumber?.toString()],
    queryFn: () => fetchPositions(client as PublicClient, liveData!.tokens, liveData!.trades, account as Address),
    enabled: Boolean(client && account && liveData && liveData.tokens.length > 0),
    staleTime: MARKET_POLL_MS - 2_000,
    retry: 1,
  });

  const positions = positionsQuery.data;

  useEffect(() => {
    // `realizedPnlEth` and `feesEarnedEth` need a full-history accounting of every
    // exit and every fee-split payout; neither is derivable from the reads this
    // adapter makes, so they stay 0 rather than becoming a plausible-looking guess.
    const activity = account
      ? (liveData?.feed ?? []).filter((a) => a.actor.toLowerCase() === account.toLowerCase())
      : [];
    const launches = account
      ? (liveData?.tokens ?? []).filter((t) => t.creator.toLowerCase() === account.toLowerCase())
      : [];
    store.applyUser({
      address: (account ?? ZERO_ADDRESS) as Hex,
      // No on-chain handle registry, and ENS is a separate lookup this adapter
      // does not make. Empty, never invented.
      handle: '',
      balanceEth: nativeBalance ? weiToNumber(nativeBalance.value) : 0,
      joinedAt: activity.length > 0 ? Math.min(...activity.map((a) => a.ts)) : Date.now(),
      positions: positions ?? [],
      launches,
      activity,
      realizedPnlEth: 0,
      feesEarnedEth: 0,
    });
  }, [store, account, nativeBalance, positions, liveData]);

  /* ── assembled api ────────────────────────────────────────────────────── */

  const error = useMemo(() => {
    if (!hasFactoryHere) {
      return `No factory deployed on ${chainName(chainId)}. Switch to a supported network.`;
    }
    if (!client) return `No RPC transport configured for ${chainName(chainId)}.`;
    if (marketQuery.error) {
      const message = marketQuery.error instanceof Error ? marketQuery.error.message : String(marketQuery.error);
      return `Could not read the launchpad on ${chainName(chainId)}: ${message}`;
    }
    return undefined;
  }, [hasFactoryHere, client, chainId, marketQuery.error]);

  // `ready` is false only while the FIRST load is outstanding. A missing factory
  // is a settled answer, not a pending one — the UI should render its explanation
  // rather than spin forever.
  const ready = !hasFactoryHere || !client || marketQuery.isSuccess || marketQuery.isError;

  /**
   * The getters are RE-CREATED on every commit here, unlike the demo path where
   * they are deliberately identity-stable.
   *
   * That is not an oversight, it is the load-bearing difference between a
   * synchronous source and an asynchronous one. Consumers memoise on callback
   * identity — `Token.tsx` does
   *
   *     const token = useMemo(() => getToken(address), [getToken, address]);
   *
   * With a stable `getToken` that memo never recomputes. In demo mode that is
   * harmless because `getWorld()` is synchronous: the first render already has
   * every token. On a live chain the first render has NONE, so a stable identity
   * latched `undefined` permanently and every token page rendered "Token not
   * found" even after the data arrived.
   *
   * So on this path callback identity is the change signal. It costs nothing: the
   * context value already changes per commit, so every consumer re-renders
   * anyway, and a live commit happens at most every ~12s rather than twice a
   * second.
   */
  const value = useMemo<MarketApi>(
    () => ({
      isDemo: false,
      ready,
      chainId,
      error,
      tokens: snapshot.tokens,
      // A failed or absent read must not leave the hero showing a previous chain's
      // totals, so the stats fall back to a real (empty) aggregate.
      stats: hasFactoryHere ? snapshot.stats : fetchStats([]),
      user: snapshot.user,
      feed: snapshot.feed,
      floorLockedSeries: snapshot.floorLockedSeries,
      getToken: (address?: string) => store.getToken(address),
      getCandles: (address?: string, interval: CandleInterval = '1h') => store.getCandles(address, interval),
      getTrades: (address?: string, limit?: number) => store.getTrades(address, limit),
      getHolders: (address?: string, limit?: number) => store.getHolders(address, limit),
      getSparkline: (address?: string) => store.getSparkline(address),
      getFloorSparkline: (address?: string) => store.getFloorSparkline(address),
      quoteBuy: (address: string | undefined, ethIn: number) => store.quoteBuy(address, ethIn),
      quoteSell: (address: string | undefined, tokensIn: number) => store.quoteSell(address, tokensIn),
      quoteRedeem: (address: string | undefined, tokensIn: number) => store.quoteRedeem(address, tokensIn),
      submitTrade: () => store.submitTrade(),
      launchToken: () => store.launchToken(),
    }),
    [store, ready, chainId, error, hasFactoryHere, snapshot],
  );

  return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>;
}

export function useMarket(): MarketApi {
  const ctx = useContext(MarketContext);
  if (!ctx) throw new Error('useMarket must be used inside <MarketProvider>');
  return ctx;
}
